import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "../context/LanguageProvider";

export default function VariantSelector({ 
  variants, 
  onVariantChange, 
  initialSelectedVariants = {}, 
  index = 0 
}) {
  const { t } = useLanguage();
  const [selectedVariants, setSelectedVariants] = useState({});
  const sizeRank = {
    xxs: 0,
    xs: 1,
    s: 2,
    m: 3,
    l: 4,
    xl: 5,
    xxl: 6,
    xxxl: 7,
    xxxxl: 8,
    xxxxxl: 9,
    xxxxxxl: 10
  };

  // Transform variants data into a more usable format
  const transformedVariants = useMemo(() => {
    const result = {};
    
    variants.forEach(group => {
      group.details.forEach(detail => {
        const type = detail.variant.type;
        if (!result[type]) {
          result[type] = [];
        }
        
        // Check if this variant already exists in the type array
        const exists = result[type].some(v => v.id === detail.variant.id);
        if (!exists) {
          result[type].push({
            ...detail.variant,
            product_variant_id: group.id
          });
        }
      });
    });
    
    return result;
  }, [variants]);

  // Initialize selected variants, validating against available variants
  useEffect(() => {
    const validatedVariants = {};
    
    Object.entries(initialSelectedVariants).forEach(([type, variant]) => {
      // Check if this variant type exists in available variants
      if (transformedVariants[type]) {
        // Find if this specific variant exists
        const existingVariant = transformedVariants[type].find(
          v => v.id === variant.id
        );
        
        if (existingVariant) {
          validatedVariants[type] = existingVariant;
        } else {
          // If variant doesn't exist, select the first available variant of this type
          validatedVariants[type] = transformedVariants[type][0];
        }
      }
    });

    setSelectedVariants(validatedVariants);
    
    // Notify parent if we had to modify the initial variants
    if (Object.keys(validatedVariants).length !== Object.keys(initialSelectedVariants).length) {
      onVariantChange(validatedVariants, index);
    }
  }, [variants, initialSelectedVariants, index, onVariantChange, transformedVariants]);

  // Get compatible variants based on current selections
  const compatibleVariants = useMemo(() => {
    const result = { ...transformedVariants };
    
    // If no variants are selected, return all variants
    if (Object.keys(selectedVariants).length === 0) {
      return result;
    }
    
    // Find all variant groups that contain all currently selected variants
    const compatibleGroups = variants.filter(group => {
      return Object.entries(selectedVariants).every(([type, variant]) => {
        return group.details.some(
          detail => detail.variant.type === type && detail.variant.id === variant.id
        );
      });
    });
    
    // Get all variants from compatible groups
    const allCompatibleVariants = compatibleGroups.flatMap(group => 
      group.details.map(detail => ({
        ...detail.variant,
        product_variant_id: group.id
      }))
    );
    
    // Filter variants to only include those that are compatible
    Object.keys(result).forEach(type => {
      result[type] = result[type].filter(variant => 
        allCompatibleVariants.some(
          v => v.type === variant.type && v.id === variant.id
        )
      );
    });
    
    return result;
  }, [variants, selectedVariants, transformedVariants]);

  const handleVariantSelect = (type, variant) => {
    const newSelectedVariants = { ...selectedVariants };
    
    // Toggle selection - if clicking an already selected variant, remove it
    if (newSelectedVariants[type]?.id === variant.id) {
      delete newSelectedVariants[type];
    } else {
      // Add new selection
      newSelectedVariants[type] = variant;
    }

    setSelectedVariants(newSelectedVariants);
    onVariantChange(newSelectedVariants, index);
  };

  const normalizeSize = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/-/g, "");

    // Convert numeric XL notation to letter notation:
    // 2xl -> xxl, 3xl -> xxxl, etc.
    const xlMatch = normalized.match(/^(\d+)xl$/);
    if (xlMatch) {
      const count = Number(xlMatch[1]);
      if (!Number.isNaN(count) && count > 0) {
        return `${"x".repeat(count)}l`;
      }
    }

    return normalized;
  };

  const sortSizeVariants = (typeVariants = []) => {
    return [...typeVariants].sort((a, b) => {
      const aValue = normalizeSize(a.value);
      const bValue = normalizeSize(b.value);

      const aRank = sizeRank[aValue];
      const bRank = sizeRank[bValue];
      const aKnown = aRank !== undefined;
      const bKnown = bRank !== undefined;

      if (aKnown && bKnown) return aRank - bRank;
      if (aKnown) return -1;
      if (bKnown) return 1;

      const aNumber = Number(aValue);
      const bNumber = Number(bValue);
      const aIsNumber = !Number.isNaN(aNumber);
      const bIsNumber = !Number.isNaN(bNumber);

      if (aIsNumber && bIsNumber) return aNumber - bNumber;
      if (aIsNumber) return 1;
      if (bIsNumber) return -1;

      return aValue.localeCompare(bValue);
    });
  };

  return (
    <div className="space-y-3">
      {Object.entries(compatibleVariants).map(([type, typeVariants]) => (
        <div key={type}>
          <h3 className="text-sm font-medium text-gray-900 mb-3 capitalize">{type}</h3>
          <div className="flex flex-wrap gap-3">
            {(type === "size" ? sortSizeVariants(typeVariants) : typeVariants).map((variant) => {
              const isSelected = selectedVariants[type]?.id === variant.id;

              if (type === "color") {
                return (
                  <button
                    key={`${variant.id}-${variant.product_variant_id}`}
                    onClick={() => handleVariantSelect(type, variant)}
                    className={`relative cursor-pointer h-8 w-8 rounded-full border-2 transition-all duration-200 ${
                      isSelected
                        ? "border-[var(--color-primary-500)] ring-2 ring-[var(--color-primary-500)] ring-offset-2"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{ backgroundColor: variant.value }}
                    title={variant.name}
                  >
                    {isSelected && (
                      <svg
                        className="absolute inset-0 h-full w-full text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              }

              return (
                <button
                  key={`${variant.id}-${variant.product_variant_id}`}
                  onClick={() => handleVariantSelect(type, variant)}
                  className={`px-3 py-2 cursor-pointer text-xs font-medium rounded-lg border transition-all duration-200 ${
                    isSelected
                      ? "bg-[var(--color-primary-500)] text-white border-[var(--color-primary-500)]"
                      : "bg-white text-gray-900 border-gray-300 hover:border-[var(--color-primary-500)] hover:text-[var(--color-primary-500)]"
                  }`}
                >
                  {variant.value}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}